"use client";

import type {
  IBrandQuoteThemeFontGroup,
  QuoteFontSchema,
} from "@/@sdk/brands/brand-model";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { QuoteThemeFontLoader } from "@/components/quotes/quotes-template/quote-theme/quote-theme-font-loader";
import { buildGoogleFontsUrlForFamilies, formatFontFamily } from "@/components/quotes/quotes-template/quote-theme/quote-theme-class-maps";
import {
  ALLOWED_QUOTE_GOOGLE_FONTS,
  QUOTE_FONT_GROUP_PRESETS,
} from "@/components/quotes/quotes-template/quote-theme/quote-theme-presets";

const FONT_GROUP_PICKER_FONTS_URL = buildGoogleFontsUrlForFamilies([
  ...ALLOWED_QUOTE_GOOGLE_FONTS,
]);

export function FontGroupPicker({
  fontSchema,
  value,
  onChange,
}: {
  fontSchema: QuoteFontSchema;
  value: IBrandQuoteThemeFontGroup;
  onChange: (value: IBrandQuoteThemeFontGroup) => void;
}) {
  const filteredPresets = QUOTE_FONT_GROUP_PRESETS.filter((preset) =>
    fontSchema === "SINGLE_FONT"
      ? preset.schema === "SINGLE_FONT"
      : true
  );

  return (
    <div className="space-y-4">
      <QuoteThemeFontLoader googleFontsUrl={FONT_GROUP_PICKER_FONTS_URL} />
      <div className="grid gap-2 sm:grid-cols-2">
        {filteredPresets.map((preset) => {
          const isSelected = value.presetId === preset.presetId;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() =>
                onChange({
                  presetId: preset.presetId,
                  headingFont: preset.headingFont,
                  bodyFont:
                    fontSchema === "SINGLE_FONT"
                      ? preset.headingFont
                      : preset.bodyFont,
                })
              }
              className={cn(
                "rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
                isSelected && "border-primary ring-1 ring-primary"
              )}
            >
              <p
                className="text-base font-semibold leading-tight"
                style={{ fontFamily: formatFontFamily(preset.headingFont) }}
              >
                {preset.name}
              </p>
              <p
                className="mt-1 text-xs text-muted-foreground"
                style={{
                  fontFamily: formatFontFamily(
                    fontSchema === "DUAL_FONT"
                      ? preset.bodyFont
                      : preset.headingFont
                  ),
                }}
              >
                {fontSchema === "DUAL_FONT"
                  ? `${preset.headingFont} + ${preset.bodyFont}`
                  : preset.headingFont}
              </p>
            </button>
          );
        })}
      </div>

      {value.presetId === "custom" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Heading font</Label>
            <Select
              value={value.headingFont}
              onValueChange={(headingFont) =>
                onChange({
                  ...value,
                  headingFont,
                  bodyFont:
                    fontSchema === "SINGLE_FONT" ? headingFont : value.bodyFont,
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALLOWED_QUOTE_GOOGLE_FONTS.map((font) => (
                  <SelectItem key={font} value={font}>
                    {font}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fontSchema === "DUAL_FONT" ? (
            <div className="space-y-2">
              <Label>Body font</Label>
              <Select
                value={value.bodyFont}
                onValueChange={(bodyFont) =>
                  onChange({ ...value, bodyFont })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALLOWED_QUOTE_GOOGLE_FONTS.map((font) => (
                    <SelectItem key={font} value={font}>
                      {font}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() =>
            onChange({
              presetId: "custom",
              headingFont: value.headingFont,
              bodyFont: value.bodyFont,
            })
          }
        >
          Customise fonts
        </button>
      )}
    </div>
  );
}
